import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { marketService, organizationService } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import MarketCard from '../components/MarketCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import ScissorsMascot from '../components/ScissorsMascot';
import { Plus, Search, TrendingUp, Clock, AlertCircle } from 'lucide-react';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '../components/ui/pagination';

const Markets = () => {
  const { t, language } = useLanguage();
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [hasOrg, setHasOrg] = useState(null); // null = unbekannt, false = keiner Org beigetreten
  const [orgFilter, setOrgFilter] = useState('all'); // Org-Filter-Chip (nur bei mehreren Orgs)

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 18;

  useEffect(() => {
    fetchMarkets();
    organizationService.getAll()
      .then(res => setHasOrg((res.data || []).some(o => o.membership === 'member')))
      .catch(() => setHasOrg(true)); // im Fehlerfall nicht blockieren
  }, []);

  const fetchMarkets = async () => {
    try {
      const response = await marketService.getAll({ limit: 1000 });
      setMarkets(response.data);
    } catch (error) {
      console.error('Failed to fetch markets:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeTab, sortBy]);

  // Distinct Orgs im aktuellen Feed -> Filter-Chips + Badges nur bei Mischung
  const feedOrgs = Array.from(
    new Map(markets.filter(m => m.organization_id).map(m => [m.organization_id, m.organization_name])).entries()
  ).map(([id, name]) => ({ id, name }));
  const multiOrg = feedOrgs.length > 1;

  const filteredMarkets = markets.filter(market => {
    // Soft-geloeschte Maerkte gehoeren nie in die allgemeine Uebersicht
    // (sie bleiben aber im Admin-Board sichtbar). Greift auch bei Cache/Live-State.
    if (market.status === 'deleted') return false;
    if (orgFilter !== 'all' && market.organization_id !== orgFilter) return false;

    const matchesSearch = market.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (market.description || '').toLowerCase().includes(searchQuery.toLowerCase());

    if (activeTab === 'all') return matchesSearch;
    return matchesSearch && market.status === activeTab;
  }).sort((a, b) => {
    if (sortBy === 'newest') {
      return new Date(b.created_at) - new Date(a.created_at);
    }
    if (sortBy === 'popular') {
      return (b.total_bets || 0) - (a.total_bets || 0);
    }
    if (sortBy === 'closing') {
      const aTime = new Date(a.end_date).getTime();
      const bTime = new Date(b.end_date).getTime();
      const now = Date.now();
      // Only filter future ones for closing soon
      if (aTime < now && bTime < now) return bTime - aTime;
      if (aTime < now) return 1;
      if (bTime < now) return -1;
      return aTime - bTime;
    }
    return 0;
  });

  const openCount = markets.filter(m => m.status === 'open').length;
  const resolvedCount = markets.filter(m => m.status === 'resolved').length;

  const totalPages = Math.ceil(filteredMarkets.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedMarkets = filteredMarkets.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('ellipsis1');
      }
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('ellipsis2');
      }
      
      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }
    
    return pages.map((page, index) => {
      if (page === 'ellipsis1' || page === 'ellipsis2') {
        return (
          <PaginationItem key={`ellipsis-${index}`}>
            <PaginationEllipsis />
          </PaginationItem>
        );
      }
      
      return (
        <PaginationItem key={page}>
          <PaginationLink
            isActive={currentPage === page}
            onClick={() => setCurrentPage(page)}
            className="cursor-pointer"
          >
            {page}
          </PaginationLink>
        </PaginationItem>
      );
    });
  };

  if (loading) {
    return (
      <div className="app-container" data-testid="markets-loading">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="space-y-6">
            <Skeleton className="h-10 w-64" />
            <div className="markets-grid">
              {Array.from({ length: 18 }).map((_, i) => (
                <Skeleton key={i} className="h-64" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Neu-User-Gate: ohne Org-Mitgliedschaft gibt es nichts zu sehen -> Beitritt anbieten
  if (hasOrg === false) {
    return (
      <div className="app-container" data-testid="markets-no-org">
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-primary/10 flex items-center justify-center">
            <AlertCircle className="w-7 h-7 text-primary" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Willkommen bei Scherenmarket</h1>
          <p className="text-muted-foreground mb-6">
            Um Wetten zu sehen und mitzumachen, musst du zuerst einer Organisation beitreten.
          </p>
          <Link to="/organizations">
            <Button size="lg" className="w-full">Organisation beitreten</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container" data-testid="markets-page">

      <main className="max-w-7xl mx-auto px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-center md:text-left">
            <h1 className="text-2xl sm:text-3xl font-bold">{t('browseMarkets')}</h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">
              {t('browseBet')}
            </p>
          </div>

          <Link to="/create-market" className="w-full md:w-auto">
            <Button className="w-full bg-red-600 hover:bg-red-700 gap-2" data-testid="create-market-btn">
              <Plus className="w-4 h-4" />
              {t('createMarket')}
            </Button>
          </Link>
        </div>

        {/* Org-Filter (nur wenn der Feed mehrere Organisationen mischt) */}
        {multiOrg && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setOrgFilter('all')}
              className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${orgFilter === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'border-border/50 text-muted-foreground hover:bg-secondary/40'}`}
            >
              {language === 'de' ? 'Alle' : 'All'}
            </button>
            {feedOrgs.map(o => (
              <button
                key={o.id}
                onClick={() => setOrgFilter(o.id)}
                className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${orgFilter === o.id ? 'bg-primary text-primary-foreground border-primary' : 'border-border/50 text-muted-foreground hover:bg-secondary/40'}`}
              >
                {o.name}
              </button>
            ))}
          </div>
        )}

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('searchMarkets')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-markets-input"
            />
          </div>
          <div className="flex gap-2">
            <Button 
              variant={sortBy === 'newest' ? 'secondary' : 'outline'} 
              size="sm" 
              onClick={() => setSortBy('newest')}
              className="gap-1 text-xs"
            >
              <Plus className="w-3 h-3" />
              {language === 'de' ? 'Neu' : 'New'}
            </Button>
            <Button 
              variant={sortBy === 'popular' ? 'secondary' : 'outline'} 
              size="sm" 
              onClick={() => setSortBy('popular')}
              className="gap-1 text-xs"
            >
              <TrendingUp className="w-3 h-3" />
              {language === 'de' ? 'Beliebt' : 'Popular'}
            </Button>
            <Button 
              variant={sortBy === 'closing' ? 'secondary' : 'outline'} 
              size="sm" 
              onClick={() => setSortBy('closing')}
              className="gap-1 text-xs"
            >
              <Clock className="w-3 h-3" />
              {language === 'de' ? 'Bald Ende' : 'Closing'}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full sm:max-w-md grid-cols-3">
            <TabsTrigger value="all" data-testid="tab-all" className="text-xs sm:text-sm">
              {t('all')} ({markets.length})
            </TabsTrigger>
            <TabsTrigger value="open" data-testid="tab-open" className="text-xs sm:text-sm">
              {t('statusOpen')} ({openCount})
            </TabsTrigger>
            <TabsTrigger value="resolved" data-testid="tab-resolved" className="text-xs sm:text-sm">
              {t('statusResolved')} ({resolvedCount})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {paginatedMarkets.length > 0 ? (
              <>
                <div className="markets-grid">
                  {paginatedMarkets.map(market => (
                    <MarketCard key={market.id} market={market} showOrgBadge={multiOrg} />
                  ))}
                </div>
                
                {totalPages > 1 && (
                  <Pagination className="mt-8">
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} 
                          className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          label={t('previous')}
                        />
                      </PaginationItem>
                      
                      {renderPageNumbers()}
                      
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} 
                          className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          label={t('next')}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
              </>
            ) : (
              <div className="empty-state">
                <ScissorsMascot size="lg" className="empty-state-icon" />
                <h3 className="text-xl font-bold mb-2">{t('noMarketsFound')}</h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery 
                    ? `${t('noMarketsMatch')} "${searchQuery}"`
                    : t('beFirstToCreate')
                  }
                </p>
                {!searchQuery && (
                  <Link to="/create-market">
                    <Button className="bg-red-600 hover:bg-red-700">
                      {t('createMarket')}
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Markets;
